import React, { useState, useRef, useEffect } from 'react';

const BaseSelect = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = "Selecione uma opção",
  required = false,
  disabled = false,
  error = null,
  description = null,
  className = "",
  icon = null,
  valueKey = "id",
  labelKey = "nome",
  emptyOption = true,
  searchable = true,
  multiple = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef(null);
  const inputRef = useRef(null);
  const selectId = `select-${name?.replace(/\s+/g, '-').toLowerCase()}`;
  const errorId = `${selectId}-error`;
  const descriptionId = `${selectId}-description`;

  // Função para normalizar strings removendo acentos e caracteres especiais
  const normalizeString = (str) => {
    if (!str) return '';
    return str
      .normalize('NFD') // Decompõe caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // Remove diacríticos (acentos)
      .toLowerCase()
      .trim();
  };

  const getValue = (option) => {
    if (!option) return '';
    // Usa nullish coalescing para permitir string vazia ('') como valor válido
    // Evita retornar o objeto inteiro quando o value é ''
    return (option[valueKey] ?? option.id ?? option.id ?? option ?? '');
  };

  // Sempre devolve string: rótulos numéricos (ex.: numeroanexo) quebravam o .toLowerCase()
  const getLabel = (option) => {
    if (option === null || option === undefined) return '';
    if (typeof option !== 'object') return String(option);
    const label = [option[labelKey], option.nome, option.name]
      .find(l => l !== null && l !== undefined && l !== '');
    return label === undefined || typeof label === 'object' ? '' : String(label);
  };

  // Busca o label da opção selecionada
  const getSelectedLabel = () => {
    if (!value) return '';
    if (multiple && Array.isArray(value)) {
      // Filtrar "todos" da contagem
      const filteredValues = value.filter(v => String(v) !== 'todos');
      return filteredValues.length > 0 ? `${filteredValues.length} item(ns) selecionado(s)` : '';
    }
    const selectedOption = options.find(option => getValue(option) == value);
    return selectedOption ? getLabel(selectedOption) : '';
  };

  // Verifica se uma opção está selecionada (para modo múltiplo)
  const isSelected = (option) => {
    const optionValue = String(getValue(option));
    if (multiple && Array.isArray(value)) {
      return value.map(v => String(v)).includes(optionValue);
    }
    return String(value) === optionValue;
  };

  // Remove um item selecionado (para modo múltiplo)
  const removeSelectedItem = (e, itemValue) => {
    e.stopPropagation();
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter(v => String(v) !== String(itemValue));
      onChange({ target: { name, value: newValue } });
    }
  };

  // Inicializa filteredOptions
  useEffect(() => {
    setFilteredOptions(options.filter(option => option && getLabel(option)));
  }, [options]);

  // Filtra as opções baseado no termo de busca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options.filter(option => option && getLabel(option)));
    } else {
      const normalizedSearchTerm = normalizeString(searchTerm);
      const filtered = options.filter(option => {
        if (!option || !getLabel(option)) return false;
        const normalizedLabel = normalizeString(getLabel(option));
        return normalizedLabel.includes(normalizedSearchTerm);
      });
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setIsFocused(true);
      if (searchable && !isOpen) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const handleOptionClick = (option) => {
    const optionValue = String(getValue(option));
    
    if (multiple) {
      // Modo múltiplo: adiciona ou remove da lista
      const currentValues = Array.isArray(value) ? value.map(v => String(v)) : [];
      let newValue;
      
      // Verificar se é a opção "Todos"
      if (optionValue === 'todos' || getLabel(option).toLowerCase() === 'todos') {
        // Selecionar todos os itens (exceto a opção "Todos" em si)
        const allValues = options
          .filter(opt => {
            const val = String(getValue(opt));
            const label = getLabel(opt).toLowerCase();
            return val !== 'todos' && label !== 'todos';
          })
          .map(opt => String(getValue(opt)));
        
        // Se todos já estão selecionados, desmarcar todos
        const allSelected = allValues.every(val => currentValues.includes(val));
        newValue = allSelected ? [] : allValues;
      } else {
        if (currentValues.includes(optionValue)) {
          // Remove se já está selecionado
          newValue = currentValues.filter(v => v !== optionValue);
        } else {
          // Adiciona se não está selecionado
          newValue = [...currentValues, optionValue];
        }
      }
      
      onChange({ target: { name, value: newValue } });
      // Não fecha o dropdown em modo múltiplo
      setSearchTerm('');
    } else {
      // Modo simples: substitui o valor
      onChange({ target: { name, value: optionValue } });
      setIsOpen(false);
      setSearchTerm('');
      setIsFocused(false);
      setHighlightedIndex(-1);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setIsFocused(false);
      setHighlightedIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        handleOptionClick(filteredOptions[highlightedIndex]);
      } else if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  return (
    <div className={`flex flex-col space-y-1 relative ${className}`} ref={selectRef}>
      {label && (
        <label
          htmlFor={selectId}
          className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="campo obrigatório">*</span>}
        </label>
      )}

      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none z-10">
            {icon}
          </div>
        )}

        {/* Input de busca ou display value */}
        <div
          className={`
            w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg 
            text-gray-900 dark:text-white 
            focus-within:outline-none focus-within:ring-2 focus-within:ring-vox-green-500 focus-within:border-vox-green-500 
            transition-all duration-200 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
            ${icon ? 'pl-10' : ''}
            ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
            ${error ? 'border-red-500 focus-within:ring-red-500 focus-within:border-red-500' : ''}
            ${multiple ? 'min-h-[44px]' : ''}
          `}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleInputClick();
          }}
          onKeyDown={!searchable || !isOpen ? handleKeyDown : undefined}
          tabIndex={disabled ? -1 : 0}
        >
          {searchable && isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Digite para pesquisar..."
              className="w-full bg-transparent outline-none"
              autoComplete="off"
            />
          ) : (
            <div className="flex items-center justify-between">
              {multiple && Array.isArray(value) && value.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {value
                    .filter(val => String(val) !== 'todos') // Não exibir "todos" nas tags
                    .map((val) => {
                      const selectedOption = options.find(opt => String(getValue(opt)) === String(val));
                      return selectedOption ? (
                        <span
                          key={val}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-vox-green-100 dark:bg-vox-green-900/30 text-vox-green-800 dark:text-vox-green-200 rounded text-sm"
                        >
                          {getLabel(selectedOption)}
                          <button
                            onClick={(e) => removeSelectedItem(e, val)}
                            className="hover:bg-vox-green-200 dark:hover:bg-vox-green-800 rounded-full p-0.5"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ) : null;
                    })}
                </div>
              ) : (
                <span className={getSelectedLabel() ? '' : 'text-gray-500 dark:text-gray-400'}>
                  {getSelectedLabel() || placeholder}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Ícone do dropdown */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Dropdown de opções */}
        {isOpen && !disabled && (
          <div className="absolute z-[99999] w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {emptyOption && (!searchable || !searchTerm) && (
              <div
                className="px-4 py-3 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                onClick={() => handleOptionClick({ [valueKey]: '', [labelKey]: '' })}
              >
                {placeholder}
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.filter(option => option && getLabel(option)).map((option, index) => {
                const selected = isSelected(option);
                const isTodosOption = String(getValue(option)) === 'todos' || getLabel(option).toLowerCase() === 'todos';
                
                // Para opção "Todos", verificar se todos os outros itens estão selecionados
                let todosChecked = false;
                if (isTodosOption && multiple) {
                  const allOtherValues = options
                    .filter(opt => {
                      const val = String(getValue(opt));
                      const label = getLabel(opt).toLowerCase();
                      return val !== 'todos' && label !== 'todos';
                    })
                    .map(opt => String(getValue(opt)));
                  const currentValues = Array.isArray(value) ? value.map(v => String(v)) : [];
                  todosChecked = allOtherValues.length > 0 && allOtherValues.every(val => currentValues.includes(val));
                }
                
                return (
                  <div
                    key={index}
                    className={`
                      px-4 py-3 cursor-pointer transition-colors duration-150
                      ${index === highlightedIndex ? 'bg-vox-blue-50 dark:bg-vox-blue-900/20' : 'hover:bg-vox-green-50 dark:hover:bg-vox-green-900/20'}
                      ${selected ? 'bg-vox-green-100 dark:bg-vox-green-900/30 text-vox-green-800 dark:text-vox-green-200' : 'text-gray-900 dark:text-white'}
                      ${isTodosOption ? 'font-semibold border-b border-gray-200 dark:border-gray-600' : ''}
                      ${multiple ? 'flex items-center gap-2' : ''}
                    `}
                    onClick={() => handleOptionClick(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={isTodosOption ? todosChecked : selected}
                        onChange={() => {}}
                        className="w-4 h-4 rounded border-gray-300 text-vox-green-600 focus:ring-vox-green-500 pointer-events-none"
                      />
                    )}
                    {getLabel(option)}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-3 text-gray-500 dark:text-gray-400">
                Nenhuma opção encontrada
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div
          className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
          id={errorId}
          role="alert"
          aria-live="polite"
        >
          <span>⚠</span>
          {error}
        </div>
      )}

      {description && (
        <div
          className="text-xs text-gray-500 dark:text-gray-400"
          id={descriptionId}
        >
          {description}
        </div>
      )}
    </div>
  );
};

export default BaseSelect; 